import { Alert, Button, Drawer, SvgIcon } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { useVariantSelector } from '../../../state/SplitTests/selectors/useVariantSelector';
import { useSegmentIdSelector } from './state/selectors/useSegmentIdSelector';
import { useOpenSplitTestDrawer } from './hooks/useOpenSplitTestDrawer';
import { DisplaySegmentationConfiguration } from './components/DisplaySegmentationConfiguration';
import { SelectTemplateControl } from './components/SharedDrawerControls/SelectTemplateControl';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import { useIsSplitTestControlGroupSelector } from '../../../state/SplitTests/selectors/useIsSplitTestControlGroupSelector';
import { useSplitTestIdByVariantIdSelector } from '../../../state/SplitTests/selectors/useSplitTestIdByVariantIdSelector';
import { DefaultVariant } from '../components/DefaultVariant';
import { WorkflowBadge } from '../../Canvas/components/WorkflowBadge';
import { Triggers } from '../Trigger/components/Triggers';
import { useMessageTriggers } from './hooks/useMessageTriggers';

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  span {
    margin-left: ${({ theme }) => theme.designToken.spacing2};
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

const BackToPreviousTab = styled.div`
  font: ${({ theme }) => theme.designToken.body2Semibold};
  color: ${({ theme }) => theme.designToken.primary500};
  display: flex;
  cursor: pointer;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.designToken.spacing3};
`;

export interface DisplayWebContentDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  messageId: string;
}

export const DisplayWebContentVariantDrawer = ({ open, closeDrawer, messageId }: DisplayWebContentDrawerProps) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const variant = useVariantSelector(messageId);
  const segmentId = useSegmentIdSelector({ messageId });
  const splitTestId = useSplitTestIdByVariantIdSelector(variant?.messageId);
  const isControlGroup = useIsSplitTestControlGroupSelector(splitTestId);
  const { setTrigger, triggers } = useMessageTriggers(variant?.messageId || messageId);

  return (
    <Drawer
      header={t('onsite.editor.workflow.displayWebContentMessage.variantDrawer.title')}
      onClose={closeDrawer}
      open={open}
      footer={
        <Button
          variation="secondary"
          onClick={closeDrawer}
          fullWidth
          ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.drawer.closeButtonAria')}
        >
          <FooterContainer>
            <SvgIcon name="check" size="24" color={theme.designToken.primary500} />
            <span>{t('onsite.editor.workflow.drawerCloseButtonLabel')}</span>
          </FooterContainer>
        </Button>
      }
    >
      <DrawerContent>
        <div>
          <HeaderContainer>
            <WorkflowBadge variantId={messageId}>
              {' '}
              {t('onsite.editor.workflow.displayWebContentMessage.variantDrawer.variantName', {
                name: variant?.name,
                weight: ' ' + variant?.weight,
              }) + '%'}
            </WorkflowBadge>
            <DefaultVariant variantId={messageId} />
          </HeaderContainer>
          <BackToPreviousTab onClick={useOpenSplitTestDrawer({ splitTestId: segmentId })}>
            <SvgIcon color={theme.designToken.primary500} name={'chevron-left'} />{' '}
            {t('onsite.editor.workflow.displayWebContentMessage.variantDrawer.returnToSplitTest')}
          </BackToPreviousTab>
        </div>

        {triggers && (
          <Triggers
            title={t('onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.variantSettings.title')}
            description={t(
              'onsite.editor.workflow.triggerMessage.triggerEvent.behaviouralTriggers.variantSettings.tooltip',
            )}
            triggers={triggers}
            onTriggerChanged={setTrigger}
            id={`variant-${variant?.messageId || messageId}`}
          />
        )}

        {isControlGroup && (
          <>
            <Alert
              aria-label="VariantControlGroupAlert"
              heading={
                <div>
                  <span style={{ marginRight: '3px' }}>
                    {t('onsite.editor.workflow.splitTest.drawer.controlGroupAlert')}
                  </span>
                  <InfoTooltip displayInline content={t('onsite.editor.workflow.splitTest.drawer.tooltipContent')} />
                </div>
              }
              variation="info"
            />
          </>
        )}

        <SelectTemplateControl messageId={messageId} />

        <DisplaySegmentationConfiguration id={messageId} />
      </DrawerContent>
    </Drawer>
  );
};
